import { useEffect, useState, type ReactNode } from 'react';
import { fetchContactDetail, fetchLeadflowLeadId, type AdminContactDetail } from './api';
import { useModalDismiss } from './use-modal-dismiss';
import { formatEventTime } from './format';

// Lead metadata is free text from an imported file, so a URL in it is a claim, not a
// fact — and this console holds an admin session. Anything but http(s) is rendered as
// text rather than handed to the browser as a link.
function webUrl(raw: string): string | null {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

function ContactDetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground w-20 flex-none">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

// Everything maestro knows about one lead, plus a leadflow link only when the lead
// actually exists there — most dialed contacts were never submitted as leads, so the
// honest default is "not a lead yet" rather than a link that 404s.
export function ContactDetailsModal({
  contactId,
  onClose,
}: {
  contactId: number;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AdminContactDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 'loading' until the leadflow resolve answers; then the id (null = not a lead).
  const [leadflow, setLeadflow] = useState<'loading' | { leadId: string | null }>('loading');

  useEffect(() => {
    let cancelled = false;
    fetchContactDetail(contactId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        // Resolve the leadflow link from the detail's client + phone (the same
        // pair leadflow matches calls with). No client id → can't be a lead.
        const clientId = d.client?.throxy_client_id;
        if (!clientId) {
          setLeadflow({ leadId: null });
          return;
        }
        return fetchLeadflowLeadId(clientId, d.contact.phone).then((leadId) => {
          if (!cancelled) setLeadflow({ leadId });
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
        setLeadflow({ leadId: null });
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const c = detail?.contact;
  const linkedinHref = c?.linkedin_url ? webUrl(c.linkedin_url) : null;
  const dismiss = useModalDismiss(onClose);

  return (
    <div
      data-overlay-above
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dismiss}
        aria-label={c?.name ?? `Contact #${contactId}`}
        className="bg-card flex max-h-[85vh] w-[min(26rem,92vw)] flex-col gap-3 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold">{c?.name ?? `Contact #${contactId}`}</h2>
            {c && (c.title || c.company) && (
              <p className="text-muted-foreground truncate text-sm">
                {[c.title, c.company].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg"
            title="Close"
          >
            ✕
          </button>
        </div>

        {err && <p className="text-destructive text-xs">⚠ {err}</p>}
        {!detail && !err && <p className="text-muted-foreground text-sm italic">Loading…</p>}

        {c && (
          <div className="flex flex-col gap-1.5">
            <ContactDetailRow label="Phone">
              <span className="font-mono">{c.phone}</span>
            </ContactDetailRow>
            {c.email && (
              <ContactDetailRow label="Email">
                <a href={`mailto:${c.email}`} className="text-primary hover:underline">
                  {c.email}
                </a>
              </ContactDetailRow>
            )}
            {detail?.client?.name && (
              <ContactDetailRow label="Client">{detail.client.name}</ContactDetailRow>
            )}
            {c.persona && <ContactDetailRow label="Persona">{c.persona}</ContactDetailRow>}
            {c.location && <ContactDetailRow label="Location">{c.location}</ContactDetailRow>}
            {c.timezone && <ContactDetailRow label="Timezone">{c.timezone}</ContactDetailRow>}
            {c.linkedin_url && (
              <ContactDetailRow label="LinkedIn">
                {linkedinHref ? (
                  <a
                    href={linkedinHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {c.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                ) : (
                  c.linkedin_url
                )}
              </ContactDetailRow>
            )}
            {detail && detail.tags.length > 0 && (
              <ContactDetailRow label="Tags">{detail.tags.join(', ')}</ContactDetailRow>
            )}
            {detail?.dnc_at && (
              <ContactDetailRow label="DNC">
                <span className="text-destructive">since {formatEventTime(detail.dnc_at)}</span>
              </ContactDetailRow>
            )}
          </div>
        )}

        {detail && (
          <div className="border-t pt-3 text-sm">
            {leadflow === 'loading' && (
              <p className="text-muted-foreground italic">Checking leadflow…</p>
            )}
            {leadflow !== 'loading' && leadflow.leadId && (
              <a
                href={`https://leadflow.throxy.com/lead/${leadflow.leadId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium underline-offset-4 hover:underline"
              >
                Open in leadflow ↗
              </a>
            )}
            {leadflow !== 'loading' && !leadflow.leadId && (
              <p className="text-muted-foreground">This contact isn’t a lead in leadflow yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
